import React, { useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { Header } from '../components/header';
import { useAlert } from 'react-alert';
import MainService from '../services/Main.service';

const StartIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
);
const StopIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14 }}><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
);

const tableCustomStyles = {
    headRow: {
        style: {
            backgroundColor: '#f8fafc',
            borderBottomColor: '#e2e8f0',
            borderBottomWidth: '2px',
            minHeight: '44px',
        },
    },
    headCells: {
        style: {
            fontSize: '0.7rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: '#64748b',
            paddingLeft: '20px',
            paddingRight: '20px',
        },
    },
    cells: {
        style: {
            paddingLeft: '20px',
            paddingRight: '20px',
            fontSize: '0.875rem',
            color: '#0f172a',
        },
    },
    rows: {
        style: {
            minHeight: '54px',
            borderBottomColor: '#e2e8f0',
        },
        highlightOnHoverStyle: {
            backgroundColor: '#f8fafc',
            outline: 'none',
        },
    },
    pagination: {
        style: {
            borderTopColor: '#e2e8f0',
            color: '#64748b',
            fontSize: '0.8125rem',
        },
    },
};

function StatusBadge({ status }) {
    if (status === 1)  return <span className="status-badge running"><span className="badge-dot pulse" />Online</span>;
    if (status === 0)  return <span className="status-badge stopped"><span className="badge-dot" />Offline</span>;
    return                    <span className="status-badge pending"><span className="badge-dot" />Pending</span>;
}

function ActionButton({ row, onAction, cooldownSeconds }) {
    const isCooldown = cooldownSeconds > 0;
    
    if (row.status === -1 || isCooldown) {
        return (
            <button className="btn btn-pending" disabled>
                {isCooldown ? `Wait ${Math.ceil(cooldownSeconds / 60)}m ${cooldownSeconds % 60}s` : 'Pending'}
            </button>
        );
    }
    if (row.status === 1) {
        return (
            <button className="btn btn-stop" onClick={() => onAction(row)}>
                <StopIcon />
                Stop
            </button>
        );
    }
    return (
        <button className="btn btn-start" onClick={() => onAction(row)}>
            <StartIcon />
            Start
        </button>
    );
}

export default function ServersPage() {
    const [list, setList]           = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [serverType, setServerType] = useState('server');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [ec2Cooldowns, setEc2Cooldowns] = useState({});
    const cooldownIntervalRef = useRef(null);
    const mainService               = new MainService();
    const alert                     = useAlert();
    const intervalRef               = useRef(null);

    const online  = list.filter(s => s.status === 1).length;
    const offline = list.filter(s => s.status === 0).length;
    const pending = list.filter(s => s.status === -1).length;

    const columns = [
        {
            name: '#',
            selector: row => row.id,
            width: '70px',
            cell: row => <span className="text-mono" style={{ color: '#94a3b8' }}>#{row.id}</span>,
        },
        {
            name: 'Server Name',
            selector: row => row.Name,
            grow: 2,
            cell: row => <div style={{ display: 'flex', flexDirection: 'column' }}><span style={{ fontWeight: 500 }}>{row.Name}</span>
            {row.PublicIpAddress && <span style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '4px', fontWeight: 400 }}>Public: {row.PublicIpAddress}</span>}
            {row.PrivateIpAddress && <span style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '4px', fontWeight: 400 }}>Private: {row.PrivateIpAddress}</span>}
            </div>,
        },
        {
            name: 'Status',
            cell: row => <StatusBadge status={row.status} />,
            width: '140px',
        },
        {
            name: 'Action',
            cell: row => <ActionButton row={row} onAction={updateState} cooldownSeconds={row.InstanceId ? (ec2Cooldowns[row.InstanceId] || 0) : 0} />,
            width: '160px',
        },
    ];

    useEffect(() => {
        getData();
        intervalRef.current = setInterval(getData, 15000);
        return () => clearInterval(intervalRef.current);
    }, [serverType]);

    useEffect(() => {
        cooldownIntervalRef.current = setInterval(() => {
            setEc2Cooldowns(prev => {
                const updated = { ...prev };
                let hasChanges = false;
                Object.keys(updated).forEach(key => {
                    if (updated[key] > 0) {
                        updated[key] = updated[key] - 1;
                        hasChanges = true;
                    }
                    if (updated[key] <= 0) {
                        delete updated[key];
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }, 1000);
        return () => clearInterval(cooldownIntervalRef.current);
    }, []);

    async function getData() {
        await getAllServices(serverType);
        setIsLoading(false);
        setLastUpdated(new Date());
    }

    const handleServerTypeChange = (type) => {
        if (type !== serverType) {
            setIsLoading(true);
            setList([]);
            setServerType(type);
        }
    };

    const formatLastUpdated = () => {
        if (!lastUpdated) return '';
        return lastUpdated.toLocaleTimeString();
    };

    const updateState = async (item) => {
        try {
            console.log(item);
            const res = await mainService.updateServer(item.type, item.status === 0 ? 1 : 0, item);
            if (res.data && res.data.message) {
                alert.show(res.data.message, { timeout: 2000, type: 'success' });
            }
            if (item.type === 'server-ec2' && item.InstanceId) {
                setEc2Cooldowns(prev => ({
                    ...prev,
                    [item.InstanceId]: 120
                }));
            }
            let counter = 0;
            const poll = setInterval(() => {
                if (counter < 4) { getData(); counter++; }
                else clearInterval(poll);
            }, 5000);
        } catch (err) {
            console.error(err);
            alert.show(err.response.data.message || err.message, { timeout: 2000, type: 'error' });
        }
    };

    const getAllServices = (type) => {
        mainService.getAllServices(type).then((res) => {
            if (res.data.body.length) {
                const servers = res.data.body[0].servers.map((d, i) => ({
                    ...d,
                    id: i + 1,
                    status: d.InstanceId ? (d.State == 'stopped' || d.State == 'stopping' ? 0 : d.State == 'pending' ? -1 : 1) : res.data.body[0].status,
                    type,
                }));
                console.log(servers);
                setList(servers);
            }
        }).catch((err) => {
            console.error(err);
            alert.show(err.message, { timeout: 2000, type: 'error' });
        });
    };

    return (
        <Header title="Servers" subtitle="Manage and monitor all servers" onRefresh={getData}>
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Total</span>
                        <span className="stat-card-icon total">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 12H3l9-9 9 9h-2v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7z"/></svg>
                        </span>
                    </div>
                    <div className="stat-card-value indigo">{list.length}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Online</span>
                        <span className="stat-card-icon running">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        </span>
                    </div>
                    <div className="stat-card-value green">{online}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Offline</span>
                        <span className="stat-card-icon stopped">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        </span>
                    </div>
                    <div className="stat-card-value red">{offline}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-card-header">
                        <span className="stat-card-label">Pending</span>
                        <span className="stat-card-icon pending">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                        </span>
                    </div>
                    <div className="stat-card-value yellow">{pending}</div>
                </div>
            </div>

            <div className="table-card">
                <div className="table-toolbar">
                    <div className="table-toolbar-left">
                        <h3>All Servers</h3>
                        <p>Auto-refreshes every 15 seconds {lastUpdated && <span style={{ marginLeft: 8, color: '#64748b' }}>• Last updated: {formatLastUpdated()}</span>}</p>
                    </div>
                    <div className="table-toolbar-right">
                        <div className="toggle-group">
                            <button
                                className={`toggle-btn ${serverType === 'server' ? 'active' : ''}`}
                                onClick={() => handleServerTypeChange('server')}
                            >
                                Server
                            </button>
                            <button
                                className={`toggle-btn ${serverType === 'server-ec2' ? 'active' : ''}`}
                                onClick={() => handleServerTypeChange('server-ec2')}
                            >
                                Server EC2
                            </button>
                        </div>
                    </div>
                </div>
                <DataTable
                    columns={columns}
                    data={list}
                    progressPending={isLoading}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                    customStyles={tableCustomStyles}
                    highlightOnHover
                    noHeader
                />
            </div>
        </Header>
    );
}
